import type { StateCreator } from 'zustand';
import { loadStored, saveStored, PERSIST_KEYS } from '../hooks/usePersist';
import type { ClimateSettings, BESettings, AlertEmailSettings, AlertNotificationSettings, SettingsLogEntry } from '../types';

export type TentRole     = 'primary' | 'reference';
export type TentLocation = 'indoor' | 'outdoor' | 'basement' | 'other';
export type SensorType   = 'climate' | 'co2_only' | 'weight' | 'camera' | 'light' | 'custom';

export interface SensorConfig {
  id: string;
  tentId: string;
  type: SensorType;
  label: string;
  metrics: string[];   // ['temperature','humidity','co2'] vs.
  enabled: boolean;
  sortOrder: number;
  /** Home Assistant entity_id — e.g. "sensor.mt15_mt29_inside_tent_temperature" */
  haEntityId?: string;
  /**
   * Bu sensör "dış ortam referansı" olarak işaretlendi mi?
   * true → fetchAmbientReading bu sensörün haEntityId'sini kullanır.
   * Herhangi bir çadırdaki herhangi bir sensör referans olabilir.
   */
  isReference?: boolean;
}

export interface TentConfig {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  sortOrder: number;
  /** 'primary' = bildirim alır, 'reference' = sadece izleme */
  role: TentRole;
  location: TentLocation;
  sensors: SensorConfig[];
}

export interface SettingsSlice {
  climateSettings: ClimateSettings;
  beSettings: BESettings;
  alertEmailSettings: AlertEmailSettings;
  alertNotifSettings: AlertNotificationSettings;
  settingsLog: SettingsLogEntry[];
  activeTentId: string;
  tents: TentConfig[];

  setClimateSettings: (v: ClimateSettings | ((p: ClimateSettings) => ClimateSettings)) => void;
  setBESettings: (v: BESettings | ((p: BESettings) => BESettings)) => void;
  setAlertEmailSettings: (v: AlertEmailSettings | ((p: AlertEmailSettings) => AlertEmailSettings)) => void;
  setAlertNotifSettings: (v: AlertNotificationSettings | ((p: AlertNotificationSettings) => AlertNotificationSettings)) => void;
  setSettingsLog: (v: SettingsLogEntry[] | ((p: SettingsLogEntry[]) => SettingsLogEntry[])) => void;
  setActiveTentId: (tentId: string) => void;
  setTents: (tents: TentConfig[]) => void;
  addTent: (name: string, description?: string) => TentConfig | null;
  removeTent: (tentId: string) => void;

  updateClimateSettings: (settings: ClimateSettings) => void;
  updateBESettings: (settings: BESettings) => void;
  updateAlertEmailSettings: (settings: AlertEmailSettings) => void;
  updateAlertNotifSettings: (settings: AlertNotificationSettings) => void;
  addSettingsLog: (entry: Omit<SettingsLogEntry, 'id'>) => void;
}

const DEFAULT_ALERT_EMAIL_SETTINGS: AlertEmailSettings = {
  enabled: false,
  email: '',
  severities: { info: false, warning: true, critical: true },
};

const DEFAULT_ALERT_NOTIFICATION_SETTINGS: AlertNotificationSettings = {
  browser:  { enabled: true,  severities: { info: false, warning: true, critical: true } },
  email:    { enabled: false, severities: { info: false, warning: true, critical: true }, address: '' },
  telegram: { enabled: true,  severities: { info: false, warning: true, critical: true } },
  alertTypes: {
    climate:      true,
    device:       false,
    watchdog:     true,
    dailyReport:  true,
    condensation: true,
  },
  quietHours: {
    enabled:       false,
    startHour:     0,     // gece yarısı
    endHour:       6,     // sabah 6
    allowCritical: true,  // kritik alarmlar gece modu dinlemez
  },
};

const DEFAULT_BE_SETTINGS: BESettings = {
  'lion-mane':   { low: 60, avg: 90,  high: 120, dryRatio: 0.35 },
  'shiitake':    { low: 40, avg: 70,  high: 100, dryRatio: 0.45 },
  'reishi':      { low: 20, avg: 40,  high: 60,  dryRatio: 0.40 },
  'turkey-tail': { low: 15, avg: 30,  high: 50,  dryRatio: 0.40 },
};

export const DEFAULT_CLIMATE_SETTINGS: ClimateSettings = {
  tentId: 'tent-1',
  phase: 'fruiting',
  stage: 'fruiting',
  autoStageDetect: true,
  temperature:     { min: 17, max: 20, ideal: 18 },
  humidity:        { min: 85, max: 92, ideal: 88 },
  co2:             { min: 500, max: 950, ideal: 700 },
  vpd:             { min: 0.2, max: 0.5, ideal: 0.3 },
  dewPoint:        { min: 14, max: 18, ideal: 16 },
  humidityDeficit: { min: 1.2, max: 2.8, ideal: 1.8 },
};

const DEFAULT_TENTS: TentConfig[] = [
  {
    id: 'tent-1', name: 'Çadır 1', description: 'Ana üretim çadırı',
    isActive: true, sortOrder: 0, role: 'primary', location: 'indoor',
    sensors: [
      { id: 'sensor-tent1-climate', tentId: 'tent-1', type: 'climate',
        label: 'İklim Sensörü (İç)', metrics: ['temperature','humidity','co2'],
        enabled: true, sortOrder: 0 },
    ],
  },
  {
    id: 'tent-2', name: 'Dış Ortam Referansı', description: 'Dış ortam referans ölçümü',
    isActive: true, sortOrder: 1, role: 'reference', location: 'outdoor',
    sensors: [
      { id: 'sensor-tent2-climate', tentId: 'tent-2', type: 'climate',
        label: 'İklim Sensörü (Dış)', metrics: ['temperature','humidity'],
        enabled: true, sortOrder: 0 },
    ],
  },
];

export const createSettingsSlice: StateCreator<SettingsSlice, [], [], SettingsSlice> = (set, get) => ({
  climateSettings: loadStored(PERSIST_KEYS.CLIMATE_SETTINGS, DEFAULT_CLIMATE_SETTINGS),
  beSettings: loadStored(PERSIST_KEYS.BE_SETTINGS, DEFAULT_BE_SETTINGS),
  alertEmailSettings: loadStored(PERSIST_KEYS.ALERT_EMAIL_SETTINGS, DEFAULT_ALERT_EMAIL_SETTINGS),
  alertNotifSettings: loadStored(PERSIST_KEYS.ALERT_NOTIF_SETTINGS, DEFAULT_ALERT_NOTIFICATION_SETTINGS),
  settingsLog: loadStored(PERSIST_KEYS.SETTINGS_LOG, []),
  activeTentId: loadStored('active_tent_id', 'tent-1'),
  tents: (() => {
    const DEFAULT_SENSORS: Record<string, SensorConfig[]> = {
      'tent-1': [
        { id: 'sensor-tent1-climate', tentId: 'tent-1', type: 'climate',
          label: 'İklim Sensörü (İç)', metrics: ['temperature', 'humidity', 'co2'],
          enabled: true, sortOrder: 0 },
      ],
      'tent-2': [
        { id: 'sensor-tent2-climate', tentId: 'tent-2', type: 'climate',
          label: 'İklim Sensörü (Dış)', metrics: ['temperature', 'humidity'],
          enabled: true, sortOrder: 0 },
      ],
    };
    const DEFAULT_ROLES: Record<string, TentRole>     = { 'tent-1': 'primary',   'tent-2': 'reference' };
    const DEFAULT_LOCS:  Record<string, TentLocation> = { 'tent-1': 'indoor',    'tent-2': 'outdoor'   };

    return (loadStored('tents', DEFAULT_TENTS) as TentConfig[]).map(t => ({
      ...t,
      role:     t.role     ?? DEFAULT_ROLES[t.id]    ?? 'primary',
      location: t.location ?? DEFAULT_LOCS[t.id]     ?? 'indoor',
      // sensors boşsa (eski localStorage) bilinen tent için default sensörleri ekle
      sensors:  (t.sensors && t.sensors.length > 0)
                  ? t.sensors
                  : (DEFAULT_SENSORS[t.id] ?? []),
    }));
  })(),

  setActiveTentId: (tentId) => {
    saveStored('active_tent_id', tentId);
    set({ activeTentId: tentId });
  },
  setTents: (tents) => {
    saveStored('tents', tents);
    set({ tents });
  },
  addTent: (name, description) => {
    const current = get().tents;
    const MAX_TENTS = 3;
    if (current.length >= MAX_TENTS) return null;
    const newId = `tent-${current.length + 1}`;
    const newTent: TentConfig = {
      id: newId,
      name,
      description,
      isActive: true,
      sortOrder: current.length,
      role: 'primary',
      location: 'indoor',
      sensors: [],
    };
    const updated = [...current, newTent];
    saveStored('tents', updated);
    set({ tents: updated });
    return newTent;
  },
  removeTent: (tentId) => {
    const current = get().tents;
    if (current.length <= 1) return; // en az 1 çadır zorunlu
    const updated = current.filter(t => t.id !== tentId);
    saveStored('tents', updated);
    set({ tents: updated });
    // Silinen çadır aktifse ilk çadıra geç
    if (get().activeTentId === tentId) {
      const first = updated[0];
      if (first) {
        saveStored('active_tent_id', first.id);
        set({ activeTentId: first.id });
      }
    }
  },

  setClimateSettings: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.climateSettings) : v;
    saveStored(PERSIST_KEYS.CLIMATE_SETTINGS, next);
    return { climateSettings: next };
  }),
  setBESettings: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.beSettings) : v;
    saveStored(PERSIST_KEYS.BE_SETTINGS, next);
    return { beSettings: next };
  }),
  setAlertEmailSettings: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.alertEmailSettings) : v;
    saveStored(PERSIST_KEYS.ALERT_EMAIL_SETTINGS, next);
    return { alertEmailSettings: next };
  }),
  setAlertNotifSettings: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.alertNotifSettings) : v;
    saveStored(PERSIST_KEYS.ALERT_NOTIF_SETTINGS, next);
    return { alertNotifSettings: next };
  }),
  setSettingsLog: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.settingsLog) : v;
    saveStored(PERSIST_KEYS.SETTINGS_LOG, next);
    return { settingsLog: next };
  }),

  updateClimateSettings: (settings) => get().setClimateSettings(settings),
  updateBESettings: (settings) => get().setBESettings(settings),
  updateAlertEmailSettings: (settings) => get().setAlertEmailSettings(settings),
  updateAlertNotifSettings: (settings) => get().setAlertNotifSettings(settings),
  addSettingsLog: (entry) => get().setSettingsLog(prev => [{ ...entry, id: `log-${crypto.randomUUID()}` }, ...prev].slice(0, 50)),
});
